/**
 * @fileoverview Grid calendar view for the Period Calculator.
 * Displays a traditional month calendar with phase-colored days.
 * @module period-tracker/period-calendar
 */

/* global femcare */

femcare.periodTracker = femcare.periodTracker || {};

/**
 * @namespace femcare.periodTracker.calendar
 * @description Manages the grid calendar view display and interactions.
 */
femcare.periodTracker.calendar = {
    container: null,
    gridElement: null,
    currentMonth: null,
    selectedDate: null,
    isInitialized: false,
    _dayCellsCache: null,
    dayLabels: [],
    dayLabelsAbbreviated: [],
    phaseLabels: {},
    calendarLabel: 'Calendar',
    onDaySelect: null,

    /**
     * Initializes the calendar module.
     * @param {Object} config - Configuration object
     * @param {HTMLElement} config.container - Calendar container
     * @param {HTMLElement} config.gridElement - Grid element
     * @param {string[]} config.dayLabels - Day name labels
     * @param {Object} config.phaseLabels - Phase label strings
     * @param {string} config.calendarLabel - Calendar aria label
     * @param {Function} config.onDaySelect - Day selection callback
     */
    init: function (config) {
        this.container = config.container;
        this.gridElement = config.gridElement;
        this.dayLabels = config.dayLabels || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        this.dayLabelsAbbreviated = this.dayLabels.map(day => day.charAt(0).toUpperCase());
        this.phaseLabels = config.phaseLabels || {};
        this.calendarLabel = config.calendarLabel || 'Calendar';
        this.onDaySelect = config.onDaySelect;
        this.currentMonth = new Date();

        if (!this.container || !this.gridElement) {
            return false;
        }

        this.isInitialized = true;
        return true;
    },

    /**
     * Renders the calendar for a month.
     * @param {Date} month - Month to render
     * @param {string[]} monthNames - Localized month names
     */
    render: function (month, monthNames) {
        if (!this.isInitialized || !this.gridElement) {
            return;
        }

        this.currentMonth = new Date(month);
        const year = month.getFullYear();
        const monthIndex = month.getMonth();

        const firstDay = new Date(year, monthIndex, 1);
        const lastDay = new Date(year, monthIndex + 1, 0);
        const totalDays = lastDay.getDate();

        let firstDayOfWeek = firstDay.getDay() - 1;
        if (firstDayOfWeek === -1) firstDayOfWeek = 6;

        const cycleLogic = femcare.periodTracker.cycleLogic;
        const phaseColors = femcare.periodTracker.config.phaseColors;

        let html = `<div class="calendar-grid" role="grid" aria-label="${this.calendarLabel}">`;

        html += '<div class="calendar-grid__header-row" role="row">';
        this.dayLabelsAbbreviated.forEach(label => {
            html += `<div class="calendar-grid__header" role="columnheader">${label}</div>`;
        });
        html += '</div>';

        html += '<div class="calendar-grid__days" role="rowgroup">';

        for (let emptyCell = 0; emptyCell < firstDayOfWeek; emptyCell++) {
            html += '<div class="calendar-grid__day calendar-grid__day--empty" role="gridcell"></div>';
        }

        for (let dayCounter = 1; dayCounter <= totalDays; dayCounter++) {
            const date = new Date(year, monthIndex, dayCounter, 12, 0, 0, 0);
            const phase = cycleLogic.getPhaseForDate(date);
            const phaseColor = phaseColors[phase] || '';
            const dateString = date.toISOString().split('T')[0];
            const phaseLabel = this.phaseLabels[phase] || phase;
            const monthName = monthNames ? monthNames[monthIndex] : '';
            const formattedDate = `${dayCounter} ${monthName} ${year}`;
            const ariaLabel = `${formattedDate}, ${phaseLabel}`;

            html += `<button type="button"
                class="calendar-grid__day calendar-grid__day--active calendar-day-${phase}"
                data-date="${dateString}"
                data-phase="${phase}"
                style="background-color: ${phaseColor}"
                data-day="${dayCounter}"
                role="gridcell"
                aria-label="${ariaLabel}">
                <span class="calendar-grid__day-number" aria-hidden="true">${dayCounter}</span>
            </button>`;
        }

        html += '</div></div>';

        this.gridElement.innerHTML = html;
        this._dayCellsCache = null;

        this.applyCalendarStyling();
        this.bindDayClickEvents();
    },

    /**
     * Gets day cells with caching.
     * @returns {NodeList} Calendar day cells
     */
    _getDayCells: function () {
        if (!this._dayCellsCache) {
            this._dayCellsCache = this.gridElement.querySelectorAll('.calendar-grid__day--active');
        }
        return this._dayCellsCache;
    },

    /**
     * Binds click events to day cells.
     */
    bindDayClickEvents: function () {
        const dayCells = this._getDayCells();

        dayCells.forEach(cell => {
            cell.addEventListener('click', (event) => {
                this.handleDayClick(event.currentTarget);
            });
        });
    },

    /**
     * Handles day click.
     * @param {HTMLElement} cellElement - Clicked cell
     */
    handleDayClick: function (cellElement) {
        const dateString = cellElement.dataset.date;
        const phase = cellElement.dataset.phase;
        const day = parseInt(cellElement.dataset.day, 10);

        if (!dateString) return;

        this.selectedDate = new Date(dateString);

        const dayCells = this._getDayCells();
        dayCells.forEach(cell => {
            cell.classList.toggle('calendar-grid__day--selected', cell === cellElement);
        });

        if (this.onDaySelect) {
            this.onDaySelect({
                date: this.selectedDate,
                phase: phase,
                day: day,
                element: cellElement
            });
        }

        const event = new CustomEvent('calendar-day-selected', {
            detail: { date: this.selectedDate, phase: phase, day: day, element: cellElement }
        });
        this.container.dispatchEvent(event);
    },

    /**
     * Applies rounded border styling to consecutive phase days.
     */
    applyCalendarStyling: function () {
        const isRTL = femcare.shared && femcare.shared.util && femcare.shared.util.isRTLContext
            ? femcare.shared.util.isRTLContext()
            : false;

        const allCells = this.gridElement.querySelectorAll('.calendar-grid__day');

        for (let rowStartIndex = 0; rowStartIndex < allCells.length; rowStartIndex += 7) {
            let previousCell = null;
            let isPreviousCellColored = null;

            for (let columnIndex = 0; columnIndex < 7 && (rowStartIndex + columnIndex) < allCells.length; columnIndex++) {
                const currentCell = allCells[rowStartIndex + columnIndex];

                if (isRTL) {
                    currentCell.classList.add('period-calendar-day--rtl');
                }

                const isCurrentCellColored = this.isCellColored(currentCell);

                if (isCurrentCellColored) {
                    this.roundFirstOrLastRowCell(currentCell, columnIndex, Math.min(7, allCells.length - rowStartIndex));
                }

                if (!isCurrentCellColored && previousCell && isPreviousCellColored) {
                    previousCell.classList.add('period-calendar-day--border-radius-end');
                }

                previousCell = currentCell;
                isPreviousCellColored = isCurrentCellColored;
            }
        }
    },

    /**
     * Checks if cell has phase styling.
     * @param {HTMLElement} cell - Calendar cell
     * @returns {boolean} True if colored
     */
    isCellColored: function (cell) {
        return (
            (cell.classList.contains('calendar-day-menstruation') ||
             cell.classList.contains('calendar-day-follicular') ||
             cell.classList.contains('calendar-day-ovulation') ||
             cell.classList.contains('calendar-day-luteal')) &&
            !cell.classList.contains('calendar-grid__day--empty')
        );
    },

    /**
     * Adds border-radius to row edges.
     * @param {HTMLElement} cell - Calendar cell
     * @param {number} cellIndex - Index in row
     * @param {number} rowLength - Row length
     */
    roundFirstOrLastRowCell: function (cell, cellIndex, rowLength) {
        if (cellIndex === 0) {
            cell.classList.add('period-calendar-day--border-radius-start');
        }
        if (cellIndex === rowLength - 1) {
            cell.classList.add('period-calendar-day--border-radius-end');
        }
    },

    /**
     * Selects a date visually.
     * @param {Date} date - Date to select
     */
    selectDate: function (date) {
        if (!date || !this.isInitialized || !this.gridElement) {
            return;
        }

        this.selectedDate = new Date(date);

        requestAnimationFrame(() => {
            const dateString = date.toISOString().split('T')[0];
            const dayCells = this._getDayCells();

            dayCells.forEach(cell => {
                cell.classList.toggle('calendar-grid__day--selected', cell.dataset.date === dateString);
            });
        });
    },

    /**
     * Gets selected date.
     * @returns {Date|null} Selected date
     */
    getSelectedDate: function () {
        return this.selectedDate;
    },

    /**
     * Refreshes the calendar.
     * @param {Date} month - Month to display
     * @param {string[]} monthNames - Month names
     */
    refresh: function (month, monthNames) {
        if (this.isInitialized) {
            this.render(month, monthNames);
        }
    }
};
